import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
    MissingTranslationHandler,
    TranslateModule,
} from '@ngx-translate/core';
import { TranslationReducer } from '@/data/translation/store';
import { TranslationsDataModule } from '@/data/translation/translations-data.module';
import { hydrationMetaReducer } from '../data/persistence/hydration.reducer';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GtmDataModule } from '@/data/gtm/gtm-data.module';
import { ConfigurationReducer } from '@/data/configurations/store';
import localEs from '@angular/common/locales/es';
import localEn from '@angular/common/locales/en';
import localEnIN from '@angular/common/locales/en-IN';
import { registerLocaleData } from '@angular/common';
import { provideClientHydration } from '@angular/platform-browser';
import { LocationsModule } from '@/data/locations/locations-data.module';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { ReservationReducer } from '@/core/reservation/store/reservation.reducer';
import {
    DATE_FORMAT_PROGRESS_BAR,
    DATE_FORMAT_PROGRESS_BAR_SHORT,
} from '@/common/pipes/format-date/format-date.pipe';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { StartHoursReducer } from '@/core/reservation/store/start-hours.reducer';
import { EndHoursReducer } from '@/core/reservation/store/end-hours.reducer';
import { LocationsReducer } from '@/core/reservation/store/locations.reducer';
import { DifferentLocationsReducer } from '@/core/reservation/store/differentLocations.reducer';
import { ReservationModule } from '@/data/reservation/reservation-data.module';
import { INPUT_DATE, INPUT_DATEPICKER } from '@/common/utils/date/formats';
import { BranchesReducer } from '@/core/reservation/store/branches.reducer';
import { VehiclesReducer } from '@/core/reservation/store/vehicles.reducer';
import { CartStep1DataModule } from '@/data/cart-step1/cart-step1-data.module';
import { CartStep2InsuranceDataModule } from '@/data/cart-step2/cart-step2-insurance-data.module';
import { CartStep2ProductDataModule } from '@/data/cart-step2/cart-step2-product-data.module';
import { DetailsDataModule } from '@/data/details/details-data.module';
import { CancelationDataModule } from '@/data/cancelation/cancelation-data.module';
import { AddressDataModule } from '@/data/address/address-data.module';
import { PrecheckinDataModule } from '@/data/precheckin/precheckin-data.module';
import { PrecheckinReducer } from '@/core/reservation/store/precheckin.reducer';
import { SummaryDataModule } from '@/data/summary/summary-data.module';
import { CartDataModule } from '@/data/cart/cart-data.module';
import { UserDataModule } from '@/data/user/user-data.module';
import { UserReducer } from '@/core/user/store/user.reducer';
import { AlertService } from '@/common/services/alert/alert.service';
import { ComponentsModule } from './shared/components/components.module';
import { LocationIntReducer } from '@/core/international/store/international.reducer';
import { ApiMissingTranslationHandler } from '@/core/translations/api.missing.translation.handler';
import { RecaptchaModule } from 'ng-recaptcha';
import { environment } from '@/env/environment';
import { CustomerSupportModule } from '@/data/customer-support/customer-support-data.module';
import { RentalPolicyDataModule } from '@/data/rental-policy/rental-policy-data.module';
import { TermsUseDataModule } from '@/data/terms-use/terms-use-data.module';
import { PrivacyPolicyDataModule } from '@/data/privacy-policy/privacy-policy-data.module';
import { RefundPolicyDataModule } from '@/data/refund-policy/refund-policy-data.module';
import { SsrService } from '@/common/services/ssr/ssr.service';
import { ConfigurationDataModule } from '@/data/configurations/configuration-data.module';
import { LanguageParamInterceptor } from './language-param.interceptor';
import { CookieModule } from 'ngx-cookie';
import { PipesModule } from '../common/pipes/pipes.module';
import { PagesModule } from './pages/pages.module';
import { UpdateSummaryDataModule } from '@/data/update-summary/update-summary-data.module';
import { getLanguage } from '@/common/utils/strings/path.utils';

registerLocaleData(localEs, 'es');
registerLocaleData(localEn, 'en');
registerLocaleData(localEnIN, 'en-IN');

@NgModule({
    declarations: [AppComponent],
    imports: [
        StoreModule.forRoot(
            {
                translations: TranslationReducer,
                configuration: ConfigurationReducer,
                reservation: ReservationReducer,
                'start-hours': StartHoursReducer,
                'end-hours': EndHoursReducer,
                locations: LocationsReducer,
                differentLocations: DifferentLocationsReducer,
                branches: BranchesReducer,
                vehicles: VehiclesReducer,
                precheckin: PrecheckinReducer,
                user: UserReducer,
                intBranch: LocationIntReducer,
            },
            { metaReducers: [hydrationMetaReducer] },
        ),
        SweetAlert2Module.forRoot(),
        TranslationsDataModule,
        BrowserModule,
        AppRoutingModule,
        NgbModule,
        TranslateModule.forRoot({
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: ApiMissingTranslationHandler,
                deps: [SsrService],
            },
            defaultLanguage: 'es',
        }),
        HttpClientModule,
        CookieModule.withOptions(),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
        NgSelectModule,
        FormsModule,
        BrowserAnimationsModule,
        MatNativeDateModule,
        GtmDataModule,
        LocationsModule,
        ReservationModule,
        CartStep1DataModule,
        PrecheckinDataModule,
        CartStep2InsuranceDataModule,
        CartStep2ProductDataModule,
        DetailsDataModule,
        CancelationDataModule,
        AddressDataModule,
        SummaryDataModule,
        UpdateSummaryDataModule,
        CartDataModule,
        UserDataModule,
        ComponentsModule,
        RecaptchaModule,
        CustomerSupportModule,
        RentalPolicyDataModule,
        TermsUseDataModule,
        PrivacyPolicyDataModule,
        RefundPolicyDataModule,
        ConfigurationDataModule,
        PipesModule,
        PagesModule,
    ],
    providers: [
        provideClientHydration(),
        { provide: LOCALE_ID, useFactory: getLanguage },
        {
            provide: 'DATE_FORMAT_PROGRESS_BAR',
            useValue: DATE_FORMAT_PROGRESS_BAR,
        },
        {
            provide: 'DATE_FORMAT_PROGRESS_BAR_SHORT',
            useValue: DATE_FORMAT_PROGRESS_BAR_SHORT,
        },
        {
            provide: 'INPUT_DATE',
            useValue: INPUT_DATE,
        },
        {
            provide: 'INPUT_DATEPICKER',
            useValue: INPUT_DATEPICKER,
        },
        {
            provide: 'RECAPTCHA_SITE_KEY',
            useValue: environment.recaptcha.siteKey,
        },
        CurrentReservationService,
        AlertService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageParamInterceptor,
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
